'use client';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Suspense, useEffect, useState } from 'react';
import { TopHeaderBlock } from '@/components/cms/blocks/TopHeaderBlock';
import { BackgroundAudio } from '@/components/shared/BackgroundAudio';
import { MobileBottomNav } from '@/components/shared/MobileBottomNav';
import { Footer } from '@/components/shared/m-footer';
import { GamesMenu } from '@/components/shared/m-game-menus';
import { SearchBox } from '@/components/shared/m-searchbox';
import { TYPE_GAMES } from '@/constants/type.games';
import {
  categorySlug,
  legacyCategoryKey,
} from '@/features/game-categories/helpers';
import { useGameCategories } from '@/features/game-categories/hooks/useGameCategories';
import type { GameCategory } from '@/features/game-categories/types';
import { useGames } from '@/features/games/hooks/useGames';
import { CategoryContent } from './category-content-games/CategoryContent';
import { CategoryContentLottery } from './category-content-games/lottery';
import { EliteSlot } from './elite-slots';
import { HotGames } from './hot-games';
import { Leaderboards } from './leaderboards';
import { MisteryChoice } from './mistery-choices';
import { RecommendationGames } from './recommendation-games';
import { TopBrands } from './top-brands';

const DotLottieReact = dynamic(
  () => import('@lottiefiles/dotlottie-react').then(mod => mod.DotLottieReact),
  {
    ssr: false,
    loading: () => <div className="h-[130px] w-full" aria-hidden />,
  }
);

function GamesPageBanner({
  category,
  legacyKey,
}: {
  category?: GameCategory;
  legacyKey: string;
}) {
  if (category?.bannerVideoUrl) {
    return (
      <div className="relative h-[130px] w-full overflow-hidden bg-black">
        <video
          className="h-full w-full object-cover object-center"
          autoPlay
          loop
          muted
          playsInline
          src={category.bannerVideoUrl}
        />
      </div>
    );
  }

  if (category?.bannerImageUrl) {
    return (
      <div className="relative h-[130px] w-full overflow-hidden bg-black">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${category.bannerImageUrl}')` }}
        />
      </div>
    );
  }

  switch (legacyKey) {
    case 'slot':
      return (
        <div className="relative h-[130px] w-full overflow-hidden bg-black">
          <video
            className="h-full w-full object-cover object-center"
            autoPlay
            loop
            muted
            playsInline
            src="/Final_NovaMobile_Assets/Banner/Latest/Slot/Slot.mp4"
          />
        </div>
      );
    case 'live-casino':
      return (
        <div className="relative h-[130px] w-full overflow-hidden bg-black">
          <video
            className="h-full w-full object-cover object-center"
            autoPlay
            loop
            muted
            playsInline
            src="/Casino.mp4"
          />
        </div>
      );
    case 'fishing':
      return (
        <div className="relative h-[130px] w-full overflow-hidden bg-black">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage:
                "url('/Final_NovaMobile_Assets/Banner/Latest/Fishing/Base.webp')",
            }}
          />
          <DotLottieReact
            src="/Final_NovaMobile_Assets/Banner/Latest/Fishing/Fishing_effect.lottie"
            loop
            autoplay
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        </div>
      );
    case 'lottery':
      return (
        <div className="relative h-[130px] w-full overflow-hidden bg-black">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage:
                "url('/Final_NovaMobile_Assets/Banner/Latest/Lottery/Base_01.webp')",
            }}
          />
          <DotLottieReact
            src="/Final_NovaMobile_Assets/Banner/Latest/Lottery/effect%20.lottie"
            loop
            autoplay
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        </div>
      );
    case 'mini-games':
      return (
        <div className="relative h-[130px] w-full overflow-hidden bg-black">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage:
                "url('/Final_NovaMobile_Assets/Banner/Latest/Mini%20game/Base_01.webp')",
            }}
          />
          <DotLottieReact
            src="/Final_NovaMobile_Assets/Banner/Latest/Mini%20game/effect_02.lottie"
            loop
            autoplay
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        </div>
      );
    case 'poker':
      return (
        <div className="relative h-[130px] w-full overflow-hidden bg-black">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage:
                "url('/Final_NovaMobile_Assets/Banner/Latest/Poker/Base.JPG')",
            }}
          />
          <DotLottieReact
            src="/Final_NovaMobile_Assets/Banner/Latest/Poker/effect.lottie"
            loop
            autoplay
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        </div>
      );
    case 'mahjong':
      return (
        <div className="relative h-[130px] w-full overflow-hidden bg-black">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage:
                "url('/Final_NovaMobile_Assets/Banner/Latest/Mahjong/base.jpg')",
            }}
          />
          <DotLottieReact
            src="/Final_NovaMobile_Assets/Banner/Latest/Mahjong/Effec.lottie"
            loop
            autoplay
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        </div>
      );
    default:
      return (
        <div
          className="h-[130px] w-full bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/games-banner.png')" }}
        />
      );
  }
}

const MainGames = () => {
  return (
    <>
      <HotGames />
      <TopBrands />
      <EliteSlot />
      <MisteryChoice />
      <Leaderboards />
    </>
  );
};

function MGamesPageContent() {
  const searchParams = useSearchParams();
  const paramActive = searchParams.get('active') || 'games';

  const { data: categories = [], isLoading: categoriesLoading } =
    useGameCategories();
  const activeCategory = categories.find(
    c => categorySlug(c.name) === paramActive
  );

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset search on tab change
  useEffect(() => {
    setSearch('');
    setDebouncedSearch('');
  }, [paramActive]);

  const { data: gamesPage, isLoading: gamesLoading } = useGames({
    categoryId: activeCategory?.id,
    page: 1,
    limit: 12,
  });

  const featuredGames = activeCategory ? gamesPage?.featured : undefined;
  const legacyKey = activeCategory
    ? legacyCategoryKey(activeCategory.name)
    : paramActive;

  return (
    <div
      className="flex w-full h-full flex-col bg-cover no-scrollbar"
      style={{ backgroundImage: "url('/bg-blur-games.png')" }}
    >
      <BackgroundAudio src="/game_song.mp3" />
      <TopHeaderBlock />
      <div className="flex-1 overflow-auto no-scrollbar">
        <GamesPageBanner category={activeCategory} legacyKey={legacyKey} />
        <SearchBox value={search} onChange={setSearch} />
        <GamesMenu />
        {legacyKey !== TYPE_GAMES.LOTTERY && (
          <RecommendationGames
            games={featuredGames}
            isLoading={categoriesLoading || gamesLoading}
          />
        )}

        {search.trim() ? (
          <CategoryContent
            rawSearch={search}
            search={debouncedSearch}
            category={activeCategory}
            isLoading={categoriesLoading}
          />
        ) : paramActive === 'games' ? (
          <MainGames />
        ) : paramActive === 'lottery' ? (
          <CategoryContentLottery />
        ) : paramActive === 'live-casino' ? null : (
          <CategoryContent
            rawSearch={search}
            search={debouncedSearch}
            category={activeCategory}
            isLoading={categoriesLoading}
          />
        )}
        <Footer />
      </div>

      <div>
        <MobileBottomNav />
      </div>
    </div>
  );
}

function MGamesPageFallback() {
  const tCommon = useTranslations('ui.common');
  return (
    <div
      className="flex h-dvh w-full flex-col bg-cover"
      style={{ backgroundImage: "url('/bg-blur-games.png')" }}
    >
      <TopHeaderBlock />
      <div className="flex flex-1 items-center justify-center overflow-auto">
        <div className="text-sm text-white/60">{tCommon('loading')}</div>
      </div>
      <MobileBottomNav />
    </div>
  );
}

export default function MGamesPage() {
  return (
    <Suspense fallback={<MGamesPageFallback />}>
      <MGamesPageContent />
    </Suspense>
  );
}
